import React, { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import TrackerTable from './components/TrackerTable/TrackerTable';
import Spinner from 'react-bootstrap/Spinner';
import 'bootstrap/dist/css/bootstrap.min.css';
import { getCurrMonth } from './shared/functions/dateProcessor';
import {
  filterStorageTypeMap,
  TABLE_REFETCH_INTERVAL_MILISEC,
  TrackerDataType,
  visibleIssueCols,
} from './shared/constants/constants';
import { useMediaInteractionHandler } from './shared/eventHandler/MediaInteractionEventHandler';
import { useLocation, useParams } from 'react-router-dom';
import { useServiceLogHandler } from './shared/eventHandler/ServiceLogEventHandler';
import { useFilters, useFiltersDispatch } from './components/TrackerFilter/Context/FiltersContext';
import isFilterEmpty from './shared/functions/isFilterEmpty';
import dayjs from '../src/utils/dayjs';
import MonthSelector from './components/TrackerTable/MonthSelector';
import { Box } from '@mui/material';
import TrackerTableFuzzySearch from './components/TrackerTable/TrackerTableFuzzySearch';
import TrackerTableFilter from './components/TrackerTable/TrackerTableFilter';
import { TrackerTableActionButtons } from './components/TrackerTable/TrackerTableActionButtons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getFilteredInteractions, getMonthlyInteractions } from './shared/requests/interactionsRequsts';
import { useRecentCreations } from './shared/hooks/useRecentCreations';
import { getTrackerFilterQueryFromFilters } from './components/TrackerFilter/trackerFilterHelpers';

function App({ dropdownTable, user }) {
  const [data, setData] = useState([]);
  const filtersDispatch = useFiltersDispatch();
  const { state } = useLocation();
  const recentCreations = useRecentCreations();
  const { markCreated } = recentCreations;
  const previousMonthlyIds = useRef(null);

  let month;
  if (sessionStorage.getItem('currMonth')) {
    month = sessionStorage.getItem('currMonth');
  } else {
    month = getCurrMonth();
  }

  const [currMonth, setMonth] = useState(month);
  const [isBulkEdit, setIsBulkEdit] = useState(false);
  const [selectedBulkEditRowIds, setSelectedBulkEditRowIds] = useState([]);

  const existingFilters = useFilters();

  const queryClient = useQueryClient();
  const onSuccessCallback = async () => {
    await queryClient.invalidateQueries({ queryKey: ['filteredIssues'] });
    await queryClient.invalidateQueries({ queryKey: ['monthlyIssues'] });
  };
  const serviceLogEventHandler = useServiceLogHandler({
    currMonth,
    recentCreations,
    onSuccessCallback,
  });
  const mediaInteractionEventHandler = useMediaInteractionHandler({
    currMonth,
    recentCreations,
    onSuccessCallback,
  });

  const issueParams = useParams();

  const { data: monthlyIssues, refetch: refetchMontlyIssues } = useQuery({
    queryKey: ['monthlyIssues', currMonth],
    queryFn: () => getMonthlyInteractions(currMonth, dayjs.tz.guess()),
    enabled: !state && isFilterEmpty(existingFilters),
    refetchInterval: TABLE_REFETCH_INTERVAL_MILISEC,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const filtersQueryString = useMemo(
    () => getTrackerFilterQueryFromFilters(existingFilters, TrackerDataType.MEDIA_INTERACTION),
    [existingFilters]
  );

  const { data: filteredIssues } = useQuery({
    queryKey: ['filteredIssues', filtersQueryString],
    queryFn: async () => {
      const data = await getFilteredInteractions(filtersQueryString);
      return data || [];
    },
    enabled: !!filtersQueryString,
  });

  const shouldDisableMonthSwitches = () => {
    const hasFilters = Object.keys(existingFilters).length > 0;
    const isQuickSearching = state?.queryString && state?.category;
    return hasFilters || isQuickSearching;
  };

  const handleBulkEditSelectAll = (isChecked, pageRowIds) => {
    if (isChecked) {
      setSelectedBulkEditRowIds(pageRowIds);
    } else {
      setSelectedBulkEditRowIds([]);
    }
  };

  const handleBulkEditSelectRow = (isChecked, rowId) => {
    setSelectedBulkEditRowIds((prevSelectedIds) => {
      if (isChecked) {
        return [...prevSelectedIds, rowId];
      } else {
        return prevSelectedIds.filter((id) => rowId !== id);
      }
    });
  };

  // Filter service based on url
  useEffect(() => {
    if (issueParams.issueId) {
      filtersDispatch({
        type: 'setFilters',
        payload: {
          id: issueParams.issueId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueParams]);

  useEffect(() => {
    if (!monthlyIssues && !filteredIssues) {
      return;
    }
    const storedFilter =
      user.nonPIUserSettings?.userStoredFilters?.[filterStorageTypeMap[TrackerDataType.MEDIA_INTERACTION]];
    if (!isFilterEmpty(existingFilters) && filteredIssues) {
      setData(filteredIssues);
    } else if (isFilterEmpty(existingFilters) && !state?.queryString && !storedFilter && monthlyIssues) {
      setData(monthlyIssues);
    }
  }, [monthlyIssues, filteredIssues, existingFilters, state, user.nonPIUserSettings?.userStoredFilters]);

  useEffect(() => {
    previousMonthlyIds.current = null;
  }, [currMonth]);

  useEffect(() => {
    if (!Array.isArray(monthlyIssues)) {
      return;
    }
    const storedFilter =
      user.nonPIUserSettings?.userStoredFilters?.[filterStorageTypeMap[TrackerDataType.MEDIA_INTERACTION]];
    const shouldTrack = isFilterEmpty(existingFilters) && !state?.queryString && !storedFilter;
    const nextIds = new Set(monthlyIssues.map((row) => row.id));

    if (shouldTrack && previousMonthlyIds.current) {
      const prevIds = previousMonthlyIds.current;
      monthlyIssues.forEach((row) => {
        if (!prevIds.has(row.id)) {
          markCreated(row.id);
        }
      });
    }

    previousMonthlyIds.current = nextIds;
  }, [monthlyIssues, existingFilters, state, user.nonPIUserSettings?.userStoredFilters, markCreated]);

  useEffect(() => {
    sessionStorage.setItem('currMonth', currMonth);

    if (!state) {
      if (isFilterEmpty(existingFilters)) {
        refetchMontlyIssues();
      }
    }
  }, [currMonth, existingFilters, refetchMontlyIssues, state]);

  return (
    <div className="App">
      <div className="interaction-container">
        <Box>
          <MonthSelector disabled={shouldDisableMonthSwitches()} currMonth={currMonth} onMonthChange={setMonth} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '1rem',
            marginBottom: '1rem',
            height: '32px',
          }}
        >
          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <TrackerTableFuzzySearch />
            <TrackerTableFilter
              setDataCallback={setData}
              eventHandler={mediaInteractionEventHandler}
              dropdownTable={dropdownTable}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <TrackerTableActionButtons
              eventHandler={mediaInteractionEventHandler}
              dropdownTable={dropdownTable}
              currMonth={currMonth}
              rows={data}
              isBulkEdit={isBulkEdit}
              selectedBulkEditRowIds={selectedBulkEditRowIds}
              selectedBulkEditRowsCount={selectedBulkEditRowIds.length}
              onSwitchToBulkEditView={() => {
                setIsBulkEdit(true);
              }}
              onSwitchToMonthlyView={() => {
                setIsBulkEdit(false);
                setSelectedBulkEditRowIds([]);
              }}
            />
          </Box>
        </Box>
        {data && dropdownTable ? (
          <>
            <div className="outerTable-Container">
              <TrackerTable
                rows={data}
                dropdownTable={dropdownTable}
                visibleCols={visibleIssueCols}
                eventHandler={mediaInteractionEventHandler}
                linkedEventHandler={serviceLogEventHandler}
                user={user}
                isBulkEdit={isBulkEdit}
                selectedBulkEditRowIds={selectedBulkEditRowIds}
                onBulkEditSelectAll={handleBulkEditSelectAll}
                onBulkEditSelectRow={handleBulkEditSelectRow}
              />
            </div>
          </>
        ) : (
          <div className="spinner-container">
            <Spinner animation="border" />
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
